import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useDataDictionary, type DataDictionaryTable } from '../../hooks/useDataDictionary';
import { ArrowRightIcon, CopyIcon } from '@c2fo/liquidity/icons';
import { copyToClipboard } from '../../utils/clipboard';
import { SqlResultChart } from './SqlResultChart';
import './DataDictionaryModal.css';

export interface SqlResultEntry {
  id: string;
  label: string;
  sql: string;
  csvData: string;
  columns: string[];
  rows: string[][];
  rowCount: number;
  timestampLabel?: string;
  relatedMessageId?: string;
  sourcePrompt?: string;
}

const DEFAULT_PANEL_WIDTH = 500;
const MIN_PANEL_WIDTH = 300;
const MAX_PANEL_WIDTH = 800;

interface DataDictionaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  sqlResults?: SqlResultEntry[];
  onWidthChange?: (width: number) => void;
  onJumpToMessage?: (messageId: string) => void;
  focusResultId?: string | null;
}

type PanelTab = 'dictionary' | 'results';

/**
 * Side panel component displaying available RAG tables and their descriptions,
 * with an optional Query Results tab for SQL data.
 */
export const DataDictionaryModal = ({
  isOpen,
  onClose,
  sqlResults = [],
  onWidthChange,
  onJumpToMessage,
  focusResultId,
}: DataDictionaryModalProps) => {
  const { tables, isLoading, error } = useDataDictionary();
  const [expandedTableId, setExpandedTableId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<PanelTab>('results');
  const [selectedResultId, setSelectedResultId] = useState<string | null>(null);
  const [isResultDetailView, setIsResultDetailView] = useState<boolean>(false);
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const startWidthRef = useRef(DEFAULT_PANEL_WIDTH);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDraggingRef.current) return;
      const delta = startXRef.current - e.clientX;
      const newWidth = Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, startWidthRef.current + delta));
      setPanelWidth(newWidth);
      onWidthChange?.(newWidth);
    },
    [onWidthChange],
  );

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDraggingRef.current = true;
      startXRef.current = e.clientX;
      startWidthRef.current = panelWidth;
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    },
    [panelWidth],
  );

  const filteredTables = tables.filter(
    (table) =>
      table.tableName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      table.content.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const handleTableClick = (tableId: string) => {
    setExpandedTableId((prev) => (prev === tableId ? null : tableId));
  };

  useEffect(() => {
    if (!isOpen || !focusResultId) {
      return;
    }

    const hasFocusedResult = sqlResults.some((result) => result.id === focusResultId);
    if (!hasFocusedResult) {
      return;
    }

    setActiveTab('results');
    setSelectedResultId(focusResultId);
    setIsResultDetailView(true);
  }, [focusResultId, isOpen, sqlResults]);

  useEffect(() => {
    if (sqlResults.length === 0) {
      setSelectedResultId(null);
      setIsResultDetailView(false);
      return;
    }

    const hasCurrentSelection = selectedResultId != null && sqlResults.some((result) => result.id === selectedResultId);
    if (hasCurrentSelection) {
      return;
    }

    const initialSelection =
      focusResultId && sqlResults.some((result) => result.id === focusResultId) ? focusResultId : sqlResults[0].id;
    setSelectedResultId(initialSelection);
  }, [focusResultId, selectedResultId, sqlResults]);

  const selectedResult = useMemo(
    () => sqlResults.find((result) => result.id === selectedResultId) ?? null,
    [selectedResultId, sqlResults],
  );

  useEffect(() => {
    if (isResultDetailView && selectedResult == null) {
      setIsResultDetailView(false);
    }
  }, [isResultDetailView, selectedResult]);

  const handleOpenResultDetails = useCallback((resultId: string) => {
    setSelectedResultId(resultId);
    setIsResultDetailView(true);
  }, []);

  return (
    <div className={`data-dictionary-panel ${isOpen ? 'open' : ''}`} style={{ width: panelWidth }}>
      {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
      <div className="dd-resize-handle" onMouseDown={handleResizeStart} />
      <div className="data-dictionary-panel-header">
        <h2>Data Explorer</h2>
        <button type="button" className="data-dictionary-close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>

      <div className="data-dictionary-tabs">
        <button
          type="button"
          className={`data-dictionary-tab ${activeTab === 'results' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('results');
            setIsResultDetailView(false);
          }}
        >
          {activeTab === 'results' && isResultDetailView ? '← Results' : 'Results'}
          {!isResultDetailView && sqlResults.length > 0 && (
            <span className="data-dictionary-tab-badge">{sqlResults.length}</span>
          )}
        </button>
        <button
          type="button"
          className={`data-dictionary-tab ${activeTab === 'dictionary' ? 'active' : ''}`}
          onClick={() => setActiveTab('dictionary')}
        >
          Dictionary
        </button>
      </div>

      {activeTab === 'dictionary' && (
        <>
          <div className="data-dictionary-search">
            <input
              type="text"
              placeholder="Search tables..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="data-dictionary-search-input"
            />
          </div>

          <div className="data-dictionary-content">
            {isLoading && (
              <div className="data-dictionary-loading">
                <span className="data-dictionary-spinner"></span>
                Loading tables...
              </div>
            )}

            {error && (
              <div className="data-dictionary-error">
                <strong>Error:</strong> {error.message}
              </div>
            )}

            {!isLoading && !error && filteredTables.length === 0 && (
              <div className="data-dictionary-empty">
                {searchQuery ? 'No tables match your search.' : 'No tables available.'}
              </div>
            )}

            {!isLoading && !error && filteredTables.length > 0 && (
              <div className="data-dictionary-list">
                {filteredTables.map((table) => (
                  <DataDictionaryTableItem
                    key={table.id}
                    table={table}
                    isExpanded={expandedTableId === table.id}
                    onClick={() => handleTableClick(table.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {activeTab === 'results' && (
        <div className="data-dictionary-content">
          {sqlResults.length === 0 ? (
            <div className="data-dictionary-empty">
              <p>No query results yet.</p>
              <p style={{ fontSize: '0.8125rem', color: '#9ca3af', marginTop: '4px' }}>
                SQL results will appear here after your query runs.
              </p>
            </div>
          ) : isResultDetailView && selectedResult ? (
            <div className="dd-results-layout">
              <div className="dd-result-detail-meta">
                {selectedResult.timestampLabel ?? 'Unknown time'} • {selectedResult.rowCount}{' '}
                {selectedResult.rowCount === 1 ? 'row' : 'rows'}
              </div>

              <SqlResultPanelCard
                result={selectedResult}
                onJumpToMessage={onJumpToMessage}
                isFocused={focusResultId === selectedResult.id}
              />
            </div>
          ) : (
            <div className="dd-results-layout">
              <div className="dd-results-selection-list" role="listbox" aria-label="Query artifacts">
                {sqlResults.map((result) => (
                  <button
                    key={result.id}
                    type="button"
                    role="option"
                    aria-selected={selectedResult?.id === result.id}
                    className={`dd-result-selection-item ${selectedResult?.id === result.id ? 'is-selected' : ''}`}
                    onClick={() => handleOpenResultDetails(result.id)}
                  >
                    <span className="dd-result-selection-content">
                      <span className="dd-result-selection-title">{result.sourcePrompt ?? result.label}</span>
                      <span className="dd-result-selection-meta">
                        {result.timestampLabel ?? 'Unknown time'} • {result.rowCount}{' '}
                        {result.rowCount === 1 ? 'row' : 'rows'}
                      </span>
                    </span>
                    <span className="dd-result-selection-icon-wrap" aria-hidden="true">
                      <ArrowRightIcon className="dd-result-selection-icon" />
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const SqlResultPanelCard = ({
  result,
  onJumpToMessage,
  isFocused,
}: {
  result: SqlResultEntry;
  onJumpToMessage?: (messageId: string) => void;
  isFocused?: boolean;
}) => {
  const [csvCopyStatus, setCsvCopyStatus] = useState<'idle' | 'copied'>('idle');
  const [sqlCopyStatus, setSqlCopyStatus] = useState<'idle' | 'copied'>('idle');
  const [isSqlVisible, setIsSqlVisible] = useState<boolean>(false);
  const csvCopyResetRef = useRef<number | null>(null);
  const sqlCopyResetRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (csvCopyResetRef.current !== null) {
        window.clearTimeout(csvCopyResetRef.current);
      }

      if (sqlCopyResetRef.current !== null) {
        window.clearTimeout(sqlCopyResetRef.current);
      }
    };
  }, []);

  const handleCopyCsv = async () => {
    try {
      const copied = await copyToClipboard(result.csvData);
      if (!copied) {
        setCsvCopyStatus('idle');
        return;
      }

      setCsvCopyStatus('copied');
      if (csvCopyResetRef.current !== null) {
        window.clearTimeout(csvCopyResetRef.current);
      }

      csvCopyResetRef.current = window.setTimeout(() => setCsvCopyStatus('idle'), 2000);
    } catch {
      setCsvCopyStatus('idle');
    }
  };

  const handleCopySql = async () => {
    if (!result.sql) {
      return;
    }

    try {
      const copied = await copyToClipboard(result.sql);
      if (!copied) {
        setSqlCopyStatus('idle');
        return;
      }

      setSqlCopyStatus('copied');
      if (sqlCopyResetRef.current !== null) {
        window.clearTimeout(sqlCopyResetRef.current);
      }

      sqlCopyResetRef.current = window.setTimeout(() => setSqlCopyStatus('idle'), 2000);
    } catch {
      setSqlCopyStatus('idle');
    }
  };

  return (
    <div id={`dd-result-${result.id}`} className={`dd-result-card ${isFocused ? 'is-focused' : ''}`}>
      <div className="dd-result-card-header">
        <span className="dd-result-card-title">
          📊 {result.label} ({result.rowCount} {result.rowCount === 1 ? 'row' : 'rows'})
        </span>
        <div className="dd-result-card-meta">
          {result.timestampLabel && <span>{result.timestampLabel}</span>}
          {result.relatedMessageId && onJumpToMessage && (
            <button
              type="button"
              className="dd-result-jump-btn"
              onClick={() => {
                if (!result.relatedMessageId) {
                  return;
                }

                onJumpToMessage(result.relatedMessageId);
              }}
            >
              View in chat
            </button>
          )}
        </div>
        <div className="dd-result-card-actions">
          {result.sql && (
            <button
              type="button"
              className="dd-result-sql-toggle-btn"
              onClick={() => setIsSqlVisible((prev) => !prev)}
              aria-expanded={isSqlVisible}
            >
              {isSqlVisible ? 'Hide SQL' : 'Show SQL'}
            </button>
          )}
        </div>
      </div>
      {isSqlVisible && result.sql && (
        <div className="dd-result-sql-block">
          <div className="dd-result-sql-header">
            <span className="dd-result-sql-title">SQL</span>
            <button type="button" className="dd-result-sql-copy-btn" onClick={handleCopySql} title="Copy SQL">
              <CopyIcon className="dd-result-copy-icon" />
              {sqlCopyStatus === 'copied' ? 'Copied!' : 'Copy SQL'}
            </button>
          </div>
          <pre className="dd-result-sql">{result.sql}</pre>
        </div>
      )}
      {result.rowCount > 0 ? (
        <SqlResultChart columns={result.columns} rows={result.rows} height={240} />
      ) : (
        <div className="dd-result-empty">No rows returned.</div>
      )}

      {result.rowCount > 0 && (
        <>
          <div className="dd-result-table-header">
            <span className="dd-result-table-title">Rows ({result.rowCount})</span>
            <button
              type="button"
              className="dd-result-copy-btn dd-result-table-copy-btn"
              onClick={handleCopyCsv}
              title="Copy CSV"
            >
              <CopyIcon className="dd-result-copy-icon" />
              {csvCopyStatus === 'copied' ? 'Copied!' : 'Copy CSV'}
            </button>
          </div>
          <div className="dd-result-table-wrapper">
            <table className="dd-result-table">
              <thead>
                <tr>
                  {result.columns.map((col, ci) => (
                    <th key={ci}>{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {result.rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.map((cell, ci) => (
                      <td key={ci}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

interface DataDictionaryTableItemProps {
  table: DataDictionaryTable;
  isExpanded: boolean;
  onClick: () => void;
}

const DataDictionaryTableItem = ({ table, isExpanded, onClick }: DataDictionaryTableItemProps) => {
  const contentPreview = table.content.split('\n')[0].replace(/^Table "[^"]+"\."[^"]+"\."[^"]+": /, '');

  return (
    <div className={`data-dictionary-item ${isExpanded ? 'expanded' : ''}`}>
      <button type="button" className="data-dictionary-item-header" onClick={onClick}>
        <div className="data-dictionary-item-info">
          <code className="data-dictionary-table-name">{table.tableName}</code>
          {!isExpanded && <p className="data-dictionary-preview">{contentPreview}</p>}
        </div>
        <span className="data-dictionary-item-caret">{isExpanded ? '▼' : '▶'}</span>
      </button>
      {isExpanded && (
        <div className="data-dictionary-item-content markdown-body">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{table.content}</ReactMarkdown>
        </div>
      )}
    </div>
  );
};
